import type { ReactNode } from "react";
import React from "react";

import { Avatar, Chip, Link, makeStyles } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTools, faHandPaper, faPlay, faTimesOctagon } from "@fortawesome/pro-light-svg-icons";
import {
    faEye as faEyeSolid,
    faEyeSlash as faEyeSlashSolid,
    faLowVision as faLowVisionSolid,
} from "@fortawesome/pro-solid-svg-icons";
import type { ISiteSlim } from "App/Modules/Models";
import { SiteState } from "App/Modules/Models";
import type { TFunction } from "i18next";
import { AvatarGroup } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import type { CSSProperties } from "@material-ui/core/styles/withStyles";
import type { SiteWithSurveillanceStateInfo, SiteWithIssueInfo } from "../../Models";
import { SiteSurveillanceState } from "../../Models";

const noWrap: CSSProperties = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

const useSiteCellStyles = makeStyles((theme) => {
    return {
        siteCell: {
            display: "flex",
            flexDirection: "column",
        },
        sitePrimaryRow: {
            marginBottom: theme.spacing(1),
            marginRight: "auto",
            maxWidth: "100%",
            ...noWrap,
            ...theme.typography.body1,
            color: theme.palette.text.primary,
        },
        siteSecondaryRow: {
            ...noWrap,
        },
        siteSecondaryText: {
            ...theme.typography.body2,
            color: theme.palette.text.secondary,
        },
    };
});

const letterAvatars: { [letter: string]: ReactNode } = {};

const getFirstLetterAvatar = (name: string) => {
    const letter = name?.substring(0, 1) ?? "";
    if (!letterAvatars[name]) {
        letterAvatars[name] = <Avatar>{letter}</Avatar>;
    }
    return letterAvatars[name];
};

const siteLetterAvatar = (site: ISiteSlim) => getFirstLetterAvatar(site.name);

const siteSurveillanceStateAvatar = (site: SiteWithSurveillanceStateInfo) => (
    <>
        {site.surveillanceState === SiteSurveillanceState.Armed && (
            <Avatar className="primary">
                <FontAwesomeIcon icon={faEyeSolid} />
            </Avatar>
        )}
        {site.surveillanceState === SiteSurveillanceState.Disarmed && (
            <Avatar className="disabled">
                <FontAwesomeIcon icon={faEyeSlashSolid} />
            </Avatar>
        )}
        {site.surveillanceState === SiteSurveillanceState.PartiallyArmed && (
            <Avatar>
                <FontAwesomeIcon icon={faLowVisionSolid} />
            </Avatar>
        )}
    </>
);

export const siteAvatar = (site: SiteWithSurveillanceStateInfo) => {
    if (site.surveillanceState) {
        return siteSurveillanceStateAvatar(site);
    }
    return siteLetterAvatar(site);
};

const getSiteStateIcon = (site: ISiteSlim) => {
    switch (site.siteState) {
        case SiteState.Maintenance:
            return faTools;
        case SiteState.Ended:
            return faHandPaper;
        case SiteState.NotStarted:
            return faTimesOctagon;
        default:
            return faPlay;
    }
};

const siteCellPrimaryRowInner = (site: ISiteSlim, classes: ReturnType<typeof useSiteCellStyles>) => (
    <>
        {site.name}
        <span className={classes.siteSecondaryText}>
            {site.company || site.partner ? ", " : " "}
            {site.company?.name ? site.company.name + (site.partner ? ", " : " ") : ""}
            {site.partner?.name}
        </span>
    </>
);
const siteCellPrimaryRowLinked = (site: ISiteSlim, classes: ReturnType<typeof useSiteCellStyles>) => (
    <Link
        className={classes.sitePrimaryRow}
        href={`/#!/sites/${site.id}`}
        onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => event.stopPropagation()}
    >
        {siteCellPrimaryRowInner(site, classes)}
    </Link>
);

const siteCellPrimaryRowNonLinked = (site: ISiteSlim, classes: ReturnType<typeof useSiteCellStyles>) => (
    <div className={classes.sitePrimaryRow}>{siteCellPrimaryRowInner(site, classes)}</div>
);

const siteCellPrimaryRow = (site: ISiteSlim, linkToSite: boolean, classes: ReturnType<typeof useSiteCellStyles>) =>
    linkToSite ? siteCellPrimaryRowLinked(site, classes) : siteCellPrimaryRowNonLinked(site, classes);

const siteCellSecondaryRow = (
    site: ISiteSlim | SiteWithIssueInfo,
    t: TFunction,
    classes: ReturnType<typeof useSiteCellStyles>,
) => {
    return (
        <div className={classes.siteSecondaryRow}>
            {site.siteStateName !== "Normal" && (
                <Chip
                    icon={<FontAwesomeIcon icon={getSiteStateIcon(site)} fixedWidth />}
                    label={t(`Enums.SiteState.Enum${site.siteStateName}`)}
                    size="small"
                />
            )}
            {site.labelNames?.map((label) => (
                <Chip key={label} label={label} variant="outlined" size="small" />
            ))}
        </div>
    );
};

export const SiteCell = (props: { site: ISiteSlim | SiteWithIssueInfo; linkToSite: boolean }) => {
    const { site, linkToSite } = props;
    const classes = useSiteCellStyles();
    const { t } = useTranslation();
    return (
        <div className={classes.siteCell}>
            {siteCellPrimaryRow(site, linkToSite, classes)}
            {siteCellSecondaryRow(site, t, classes)}
        </div>
    );
};

export const SiteIssuesSummaryCell = ({ site }: { site: SiteWithIssueInfo }) =>
    !!(site.numIssuesError + site.numIssuesWarning + site.numIssuesOther) && (
        <AvatarGroup max={3} className="MuiAvatarGroup-inline MuiAvatarGroup-sizeSmall">
            {site.numIssuesError && <Avatar className="error">{site.numIssuesError}</Avatar>}
            {site.numIssuesWarning && <Avatar className="warning">{site.numIssuesWarning}</Avatar>}
            {site.numIssuesOther && <Avatar className="info">{site.numIssuesOther}</Avatar>}
        </AvatarGroup>
    );
